import { borderRadius, colors, spacing } from "@indeed/ifl-tokens/react-native";
import React, { useCallback, useEffect, useState } from "react";
// eslint-disable-next-line @typescript-eslint/no-restricted-imports -- Box's `sx` opacity does not apply reliably on web; View is required here.
import { Platform, StyleSheet, View } from "react-native";

import { Alert, Box, Button, Flex, Heading, Text } from "../../../../../../../mads";
import { CheckCircle, Upload } from "../../../../../../../mads/icons";
import { useI18n, useLocale } from "../../../../../../../shared/I18n";
import { notifyProfileDataChanged } from "../../../../../../../shared/lib/ResumeGlowup";
import { logNonFatal } from "../../../../../../../shared/native-modules/tare/logNonFatal";
import openResumeBuilderModal from "../../../../../../../shared/native-modules/tare/openResumeBuilderModal";
import { isWeb } from "../../../../../../../shared/util/isWeb";
import { getResumeBuilderUrl } from "../../../../../../../shared/util/profile/getResumeBuilderUrl";
import { useViewjobOneGraphModel } from "../../../../../context/ViewJobDataContext";
import { useIsDesktopDisplay } from "../../../../../context/ViewJobDisplayContext";
import { useMatchOverviewResumeUpload } from "../../../../../hooks/useMatchOverviewResumeUpload";
import { emitViewjobResumeEvent, ResumeWorkflow } from "../../../../../util/viewjobResumeEvents";
import { useLogging } from "../../context/useLogging";
import {
  MatchQualification,
  MatchQualificationVariant,
} from "../MatchOverviewQualificationCollection/MatchQualification";
import { AddResumeBottomSheet } from "./AddResumeBottomSheet";

const RESUME_BUILDER_FROM = Platform.select({
  ios: "rnviewjob-gated-match-overview-ios",
  android: "rnviewjob-gated-match-overview-android",
  default: "rnviewjob-gated-match-overview",
});

// Static placeholder rows for the greyed background list.
// Uses MatchQualification directly so the visual matches the real component.
const PLACEHOLDER_ROWS: Array<{ label: string; required: boolean }> = [
  { label: "Experience", required: true },
  { label: "Education", required: false },
  { label: "Skills", required: true },
  { label: "Certifications", required: false },
];

// Caps the CTA card so long alert copy wraps instead of widening the card.
// Matches the sibling CollectionModal card.
const CTA_CARD_MAX_WIDTH = 480;

// Below this container width the two desktop CTAs no longer fit side by side and
// would otherwise push the card wider than its pane.
const NARROW_CONTAINER_WIDTH = 420;

const noop = () => {};

export interface GatedMatchOverviewProps {
  /**
   * True when a previous hydration attempt failed to lift the gate. Renders a
   * retry prompt and suppresses the impression log, since this is a re-render
   * of an already-counted gate rather than a new impression.
   */
  hydrationError?: boolean;
  /** Called when the user presses "Try again" on the hydration error. */
  onHydrationRetry?: () => void;
  /**
   * True when the resume was saved but the backend couldn't read it, so there is
   * nothing to match against. Like `hydrationError`, this is a re-render of an
   * already-counted gate, so it suppresses the impression log.
   */
  resumeUnparseable?: boolean;
}

/**
 * A single benefit bullet item inside the CTA card.
 */
const BenefitRow: React.FC<{ label: string }> = ({ label }) => (
  <Flex sx={styles.benefitRow}>
    <Box sx={styles.benefitIcon}>
      <CheckCircle color={colors.foregroundStatusSuccess} size="xs" />
    </Box>
    <Text level={2}>{label}</Text>
  </Flex>
);

/**
 * Disabled-state variant of the Match Overview section.
 *
 * Renders the Match Overview heading with a greyed-out, non-interactive placeholder
 * requirements list in the background and a compact CTA card overlaid on top,
 * prompting the job seeker to add a resume.
 */
export const GatedMatchOverview: React.FC<GatedMatchOverviewProps> = ({
  hydrationError = false,
  onHydrationRetry,
  resumeUnparseable = false,
}) => {
  const gt = useI18n();
  const locale = useLocale();
  const logger = useLogging();
  const isDesktop = useIsDesktopDisplay();
  const profileBaseUrl = useViewjobOneGraphModel()?.jobSeeker?.profile?.profileBaseUrl ?? "";
  const [isAddResumeSheetVisible, setIsAddResumeSheetVisible] = useState(false);
  // The display surface is host-provided and says nothing about how much room we
  // actually have, so measure it. A desktop 2-pane can be narrower than a phone.
  const [containerWidth, setContainerWidth] = useState<number | null>(null);
  const [cardHeight, setCardHeight] = useState<number | null>(null);

  // Treat unmeasured as wide so the first paint matches the common case.
  const isNarrow = containerWidth !== null && containerWidth < NARROW_CONTAINER_WIDTH;
  const {
    triggerUpload,
    loading: uploading,
    error: uploadError,
    clearError: clearUploadError,
    uploadResult,
    clearUploadResult,
  } = useMatchOverviewResumeUpload();

  useEffect(() => {
    // A failed or unparseable hydration remounts this component over an impression
    // already logged before the skeleton replaced it — don't double-count it.
    if (hydrationError || resumeUnparseable) {
      return;
    }

    logger.logShowedGatedMatchOverview();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleBuildPress = useCallback(() => {
    logger.logGatedMatchOverviewBuildTap();

    // Web has no native navigator bridge to present the builder in a modal, so go to
    // the builder directly. Same tab on purpose: coming back to the job reloads the
    // page, which lifts the gate now that a resume exists.
    if (isWeb) {
      if (!profileBaseUrl) {
        void logNonFatal(
          "Cannot open the resume builder on web without a profile base URL",
          0,
          "viewjob.gatedMatchOverview.openResumeBuilderModalError",
          {},
        );
        return;
      }

      globalThis.location.assign(getResumeBuilderUrl(profileBaseUrl, locale));
      return;
    }

    void openResumeBuilderModal(true, RESUME_BUILDER_FROM)
      .then((result) => {
        if (result === "COMPLETED") {
          logger.logGatedMatchOverviewBuildCompleted();
          // An Indeed Resume is authored rather than parsed, so there is no
          // parse step that could have failed.
          emitViewjobResumeEvent("resumeWorkflowCompleted", {
            workflow: ResumeWorkflow.Build,
            parsed: true,
          });
        } else if (result === "ERROR") {
          void logNonFatal(
            "openResumeBuilderModal returned ERROR",
            0,
            "viewjob.gatedMatchOverview.openResumeBuilderModalError",
            {},
          );
        } else if (result !== "CANCELLED") {
          // CANCELLED is the seeker dismissing the modal, not a failure. Anything else
          // means the bridge gave us something we don't understand — surface it rather
          // than dropping the tap on the floor.
          void logNonFatal(
            "openResumeBuilderModal returned an unrecognized result",
            0,
            "viewjob.gatedMatchOverview.openResumeBuilderModalError",
            { result: String(result) },
          );
        }
      })
      .catch((err: unknown) => {
        void logNonFatal(
          "openResumeBuilderModal failed",
          0,
          "viewjob.gatedMatchOverview.openResumeBuilderModalError",
          { error: err instanceof Error ? err.message : String(err) },
        );
      });
  }, [logger, locale, profileBaseUrl]);

  useEffect(() => {
    if (!uploadResult) {
      return;
    }

    logger.logGatedMatchOverviewUploadCompleted(true);
    // Tells the native profile tab to refresh; the viewjob event below is what
    // hydrates this panel in place.
    notifyProfileDataChanged();
    emitViewjobResumeEvent("resumeWorkflowCompleted", {
      workflow: ResumeWorkflow.Upload,
      parsed: uploadResult.parsed,
    });
    clearUploadResult();
  }, [uploadResult, logger, clearUploadResult]);

  useEffect(() => {
    if (!uploadError) {
      return;
    }

    logger.logGatedMatchOverviewUploadCompleted(false);
  }, [uploadError, logger]);

  const handleUploadPress = () => {
    logger.logGatedMatchOverviewUploadClick();
    void triggerUpload();
  };

  return (
    <Box sx={styles.container}>
      {/* Relative wrapper — the absolute CTA card positions against this box */}
      <Box sx={styles.listWrapper}>
        <Heading level={4} sx={styles.heading} testID="vj-gated-match-overview-heading">
          {gt.gettext("Match overview")}
        </Heading>
        {/* greyedListContainer scopes ctaPositioner to the list area only,
                        so the heading stays unobstructed by the CTA card.
                        zIndex:3 keeps it above the dimOverlay (zIndex:2). */}
        <Box
          sx={[
            styles.greyedListContainer,
            // The CTA card is absolutely positioned, so it adds no height of its
            // own. Reserve room for it or it spills over the content below.
            cardHeight !== null && { minHeight: cardHeight + 2 * spacing.s5 },
          ]}
          onLayout={(event) => setContainerWidth(event.nativeEvent.layout.width)}
          testID="vj-gated-match-overview-list-container"
        >
          {/* Greyed non-interactive placeholder list — reuses MatchQualification
                            for visual parity. Hidden from the a11y tree and blocked from touch.
                            View (not Box) used here so opacity applies reliably on web. Labels are
                            hardcoded rather than translated since this content is always blurred
                            and hidden behind the CTA card. */}
          <View
            accessibilityElementsHidden={true}
            importantForAccessibility="no-hide-descendants"
            pointerEvents="none"
            style={styles.greyedList}
          >
            <Heading level={isDesktop ? 3 : 2} sx={styles.subHeading}>
              Qualifications: 0 of 10
            </Heading>
            {PLACEHOLDER_ROWS.map(({ label, required }) => (
              <MatchQualification
                key={label}
                variant={MatchQualificationVariant.Missing}
                primaryText={label}
                required={required}
                onConfirm={noop}
                onReject={noop}
              />
            ))}
            <Box sx={styles.showMoreBox}>
              <Text level={3} sx={styles.showMoreText}>
                +5 more
              </Text>
            </Box>
          </View>

          {/* Centers the CTA card over the list rows */}
          <Box sx={[StyleSheet.absoluteFill, styles.ctaPositioner]} pointerEvents="box-none">
            <Box
              sx={styles.ctaCard}
              onLayout={(event) => setCardHeight(event.nativeEvent.layout.height)}
              testID="vj-gated-match-overview-cta-card"
            >
              <Box sx={styles.benefitRows}>
                <Heading level={isDesktop ? 4 : 3}>
                  {gt.gettext("You haven't added a resume yet")}
                </Heading>
                <BenefitRow label={gt.gettext("See how well this job fits you")} />
                <BenefitRow label={gt.gettext("Apply where you're likely to hear back")} />
                <BenefitRow label={gt.gettext("Match to the right roles, right away")} />
              </Box>

              {uploadError && (
                <Alert
                  status="critical"
                  title={uploadError.title}
                  explainerText={uploadError.message}
                  dismissAction={{ ariaLabel: gt.gettext("Dismiss"), onDismiss: clearUploadError }}
                />
              )}

              {resumeUnparseable && (
                <Alert
                  status="warning"
                  title={gt.gettext("We can't read your file")}
                  explainerText={gt.gettext(
                    "We recommend that you choose a different resume or create an Indeed Resume instead.",
                  )}
                />
              )}

              {hydrationError && (
                <Alert
                  status="critical"
                  title={gt.gettext("Something went wrong on our end")}
                  explainerText={gt.gettext("We’re working to fix it. Please try again later.")}
                  actions={
                    onHydrationRetry
                      ? [{ label: gt.gettext("Retry"), onPress: onHydrationRetry }]
                      : undefined
                  }
                />
              )}

              <Flex
                sx={[styles.ctaButtonRow, isNarrow && styles.ctaButtonColumn]}
                testID="vj-gated-match-overview-cta-buttons"
              >
                {isDesktop ? (
                  <>
                    <Button
                      variant="secondary"
                      onPress={handleUploadPress}
                      loading={uploading}
                      iconBefore={<Upload size="sm" color={colors.foregroundPrimary} />}
                    >
                      {gt.gettext("Upload your resume")}
                    </Button>
                    <Button variant="text" onPress={handleBuildPress}>
                      {gt.gettext("Build your resume")}
                    </Button>
                  </>
                ) : (
                  <Button
                    variant="text"
                    loading={uploading}
                    onPress={() => setIsAddResumeSheetVisible(true)}
                  >
                    {gt.gettext("Add your resume")}
                  </Button>
                )}
              </Flex>
            </Box>
          </Box>
        </Box>

        {/* 10% black dim — covers the full container including the heading */}
        <Box sx={[StyleSheet.absoluteFill, styles.dimOverlay]} pointerEvents="none" />
      </Box>

      {/* Rendered unconditionally and driven by `visible`: unmounting the sheet
          to hide it would tear down its Modal before iOS could report the
          dismissal, and the selected action would be dropped. Renders nothing
          while hidden. */}
      <AddResumeBottomSheet
        visible={isAddResumeSheetVisible}
        onClose={() => setIsAddResumeSheetVisible(false)}
        onBuildPress={handleBuildPress}
        onUploadPress={handleUploadPress}
      />
    </Box>
  );
};

const styles = StyleSheet.create({
  container: {
    gap: spacing.s4,
    // Bleed past the horizontal padding ViewJobMainContent applies to job content
    // (CONTENT_MARGIN, 16) so the dimmed container spans the full screen width. The
    // listWrapper padding below re-indents the text to the original alignment.
    // Same negative-margin approach as JobComponent/LinkButton.
    marginHorizontal: -spacing.s4,
  },
  heading: {
    marginBlockEnd: spacing.s2,
  },
  listWrapper: {
    position: "relative",
    padding: spacing.s4,
  },
  greyedListContainer: {
    position: "relative",
    zIndex: 3,
  },
  greyedList: {
    opacity: 0.35,
    paddingBlockStart: spacing.s5,
    paddingBlockEnd: spacing.s5,
    // react-native-web doesn't support the RN 0.83 array form of `filter`,
    // so use the CSS string form — RN's native filter parser accepts it too.
    filter: "blur(3px)",
  },
  subHeading: {
    marginBlockEnd: spacing.s3,
  },
  showMoreBox: {
    marginTop: 4,
    flex: 1,
    justifyContent: "center",
    alignItems: "flex-start",
  },
  showMoreText: {
    color: colors.foreground,
    borderWidth: 0,
    borderBottomWidth: 1,
    borderStyle: "dotted",
    marginTop: 8,
  },
  dimOverlay: {
    backgroundColor: "rgba(0, 0, 0, 0.1)",
    zIndex: 2,
  },
  ctaPositioner: {
    alignItems: "center",
    justifyContent: "center",
    padding: spacing.s5,
  },
  ctaCard: {
    // No `width: "100%"` — the card stays shrink-to-fit, this is only a ceiling.
    maxWidth: CTA_CARD_MAX_WIDTH,
    paddingVertical: spacing.s5,
    paddingHorizontal: spacing.s4,
    backgroundColor: colors.background,
    borderRadius: borderRadius.lg,
    shadowColor: colors.utilityShadow,
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.12,
    shadowRadius: spacing.s2,
    elevation: 4,
    alignItems: "center",
    gap: 12,
  },
  benefitRows: {
    gap: spacing.s2,
  },
  benefitRow: {
    flexDirection: "row",
    gap: spacing.sc2,
    alignItems: "center",
  },
  benefitIcon: {
    width: spacing.s5,
    height: spacing.s5,
    alignItems: "center",
    justifyContent: "center",
    flexShrink: 0,
    borderRadius: borderRadius.full,
  },
  ctaButtonRow: {
    paddingTop: spacing.s2,
    flexDirection: "row",
    alignItems: "center",
    gap: spacing.s2,
    flexWrap: "wrap",
  },
  ctaButtonColumn: {
    flexDirection: "column",
  },
});
